/**
 * Status Page
 * 
 * Phase C8 - Library health metrics (workspace-scoped only)
 * Phase D9 - Project and question aggregations
 * Phase E9 - AI Run metrics (aggregate only)
 */

import { useEffect, useState } from 'react';
import { PageHeader, Card } from '@threadlock/ui';
import { 
  countDocumentsByStatus, 
  getLastIngestionTimestamp, 
  getTotalProjectCount,
  countProjectsByStatus,
  getTotalQuestionCount,
  listAiRunsForWorkspace,
  getLastAiRunTimestamp,
  getLastAiErrorTimestamp,
  type DocumentStatus,
  type ProjectStatus,
  type AiRunStatus
} from '@threadlock/data';
import { db } from '../../firebase-init';
import { useWorkspaceState } from '../../config/workspace';

export default function StatusPage() {
  const { activeWorkspaceId } = useWorkspaceState();
  const [counts, setCounts] = useState<Record<DocumentStatus, number> | null>(null);
  const [lastIngestion, setLastIngestion] = useState<Date | null>(null);
  const [totalProjects, setTotalProjects] = useState<number>(0);
  const [projectCounts, setProjectCounts] = useState<Record<ProjectStatus, number> | null>(null);
  const [totalQuestions, setTotalQuestions] = useState<number>(0);
  
  // Phase E9: AI metrics
  const [totalAiRuns, setTotalAiRuns] = useState<number>(0);
  const [aiRunsByStatus, setAiRunsByStatus] = useState<Record<AiRunStatus, number>>({
    blocked: 0,
    success: 0,
    failed: 0,
    failed_validation: 0,
  });
  const [lastAiRun, setLastAiRun] = useState<Date | null>(null);
  const [lastAiError, setLastAiError] = useState<Date | null>(null);
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadMetrics() {
      if (!activeWorkspaceId) {
        setLoading(false);
        return;
      }

      try {
        // Library metrics
        const statusCounts = await countDocumentsByStatus(db, activeWorkspaceId);
        setCounts(statusCounts);

        const timestamp = await getLastIngestionTimestamp(db, activeWorkspaceId);
        if (timestamp) {
          setLastIngestion(timestamp.toDate());
        }

        // Phase D: Project metrics
        const projCount = await getTotalProjectCount(db, activeWorkspaceId);
        setTotalProjects(projCount);

        const projStatusCounts = await countProjectsByStatus(db, activeWorkspaceId);
        setProjectCounts(projStatusCounts);

        // Phase D: Question metrics
        const qCount = await getTotalQuestionCount(db, activeWorkspaceId);
        setTotalQuestions(qCount);

        // Phase E9: AI Run metrics
        const aiRuns = await listAiRunsForWorkspace(db, activeWorkspaceId);
        setTotalAiRuns(aiRuns.length);
        
        // Count by status
        const statusMap: Record<AiRunStatus, number> = {
          blocked: 0,
          success: 0,
          failed: 0,
          failed_validation: 0,
        };
        aiRuns.forEach(run => {
          statusMap[run.status] = (statusMap[run.status] || 0) + 1;
        });
        setAiRunsByStatus(statusMap);

        // Last run timestamp
        const lastRunTs = await getLastAiRunTimestamp(db, activeWorkspaceId);
        if (lastRunTs) {
          setLastAiRun(lastRunTs.toDate());
        }

        // Last error timestamp
        const lastErrorTs = await getLastAiErrorTimestamp(db, activeWorkspaceId);
        if (lastErrorTs) {
          setLastAiError(lastErrorTs.toDate());
        }
      } catch (err) {
        console.error('Failed to load status metrics:', err);
      } finally {
        setLoading(false);
      }
    }

    loadMetrics();
  }, [activeWorkspaceId]);

  const totalDocuments = counts
    ? counts.uploaded + counts.processing + counts.ready + counts.failed
    : 0;

  return (
    <div className="max-w-4xl mx-auto">
      <PageHeader
        title="System Status"
        description="Workspace-scoped health metrics"
      />

      {loading ? (
        <div className="mt-8 text-center text-[var(--surface-text-muted)]">
          Loading metrics...
        </div>
      ) : (
        <>
          {/* Library Metrics */}
          <div className="mt-8">
            <h2 className="text-[var(--text-lg)] font-semibold text-[var(--surface-text-primary)] mb-4">
              Library
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Total Documents */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Total Documents
                </h3>
                <div className="text-[var(--text-3xl)] font-bold text-[var(--surface-text-primary)]">
                  {totalDocuments}
                </div>
              </Card>

              {/* Documents by Status */}
              {counts && (
                <>
                  <Card>
                    <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                      Ready Documents
                    </h3>
                    <div className="text-[var(--text-3xl)] font-bold text-[var(--status-success)]">
                      {counts.ready}
                    </div>
                  </Card>

                  <Card>
                    <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                      Processing Documents
                    </h3>
                    <div className="text-[var(--text-3xl)] font-bold text-[var(--status-warning)]">
                      {counts.processing + counts.uploaded}
                    </div>
                  </Card>

                  <Card>
                    <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                      Failed Documents
                    </h3>
                    <div className="text-[var(--text-3xl)] font-bold text-[var(--status-error)]">
                      {counts.failed}
                    </div>
                  </Card>
                </>
              )}

              {/* Last Ingestion */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Last Ingestion
                </h3>
                <div className="text-[var(--text-lg)] font-semibold text-[var(--surface-text-primary)]">
                  {lastIngestion
                    ? lastIngestion.toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : 'No ingestions yet'}
                </div>
              </Card>
            </div>
          </div>

          {/* Phase D: Project Metrics */}
          <div className="mt-8">
            <h2 className="text-[var(--text-lg)] font-semibold text-[var(--surface-text-primary)] mb-4">
              Projects
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Total Projects */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Total Projects
                </h3>
                <div className="text-[var(--text-3xl)] font-bold text-[var(--surface-text-primary)]">
                  {totalProjects}
                </div>
              </Card>

              {/* Projects by Status */}
              {projectCounts && (
                <>
                  <Card>
                    <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                      Ready Projects
                    </h3>
                    <div className="text-[var(--text-3xl)] font-bold text-[var(--status-success)]">
                      {projectCounts.ready}
                    </div>
                  </Card>

                  <Card>
                    <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                      Created / Extracting
                    </h3>
                    <div className="text-[var(--text-3xl)] font-bold text-[var(--status-warning)]">
                      {projectCounts.created + projectCounts.extracting}
                    </div>
                  </Card>

                  <Card>
                    <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                      Failed Projects
                    </h3>
                    <div className="text-[var(--text-3xl)] font-bold text-[var(--status-error)]">
                      {projectCounts.failed}
                    </div>
                  </Card>
                </>
              )}

              {/* Total Questions */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Total Questions
                </h3>
                <div className="text-[var(--text-3xl)] font-bold text-[var(--surface-text-primary)]">
                  {totalQuestions}
                </div>
              </Card>
            </div>
          </div>

          {/* Phase E9: AI Metrics */}
          <div className="mt-8">
            <h2 className="text-[var(--text-lg)] font-semibold text-[var(--surface-text-primary)] mb-4">
              AI Drafting
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Total AI Runs */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Total AI Runs
                </h3>
                <div className="text-[var(--text-3xl)] font-bold text-[var(--surface-text-primary)]">
                  {totalAiRuns}
                </div>
              </Card>

              {/* Successful Runs */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Successful
                </h3>
                <div className="text-[var(--text-3xl)] font-bold text-green-600">
                  {aiRunsByStatus.success}
                </div>
              </Card>

              {/* Blocked Runs */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Blocked (Insufficient Sources)
                </h3>
                <div className="text-[var(--text-3xl)] font-bold text-amber-600">
                  {aiRunsByStatus.blocked}
                </div>
              </Card>

              {/* Failed Runs */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Failed
                </h3>
                <div className="text-[var(--text-3xl)] font-bold text-red-600">
                  {aiRunsByStatus.failed + aiRunsByStatus.failed_validation}
                </div>
                {aiRunsByStatus.failed_validation > 0 && (
                  <p className="text-[var(--text-xs)] text-[var(--surface-text-muted)] mt-1">
                    ({aiRunsByStatus.failed_validation} citation validation failures)
                  </p>
                )}
              </Card>

              {/* Last Run */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Last AI Run
                </h3>
                <div className="text-[var(--text-sm)] text-[var(--surface-text-primary)]">
                  {lastAiRun ? lastAiRun.toLocaleString() : 'Never'}
                </div>
              </Card>

              {/* Last Error */}
              <Card>
                <h3 className="text-[var(--text-sm)] font-medium text-[var(--surface-text-secondary)] mb-2">
                  Last Error
                </h3>
                <div className="text-[var(--text-sm)] text-[var(--surface-text-primary)]">
                  {lastAiError ? lastAiError.toLocaleString() : 'Never'}
                </div>
              </Card>
            </div>
          </div>
        </>
      )}

      {/* Audit-safe Notice */}
      <Card className="mt-6">
        <p className="text-[var(--text-sm)] text-[var(--surface-text-muted)]">
          <strong>Note:</strong> This page shows workspace-scoped aggregated metrics only. No document
          IDs, filenames, storage paths, question content, or draft content are exposed for audit safety.
        </p>
      </Card>
    </div>
  );
}
